<template>
  <div class="p-relative">
    <div :ref="bodyKey" :title="ts" tabindex="0" class="message__body">
      <div
        :class="[
          {
            '--single': isSingleImage,
            '--feed': isFeed,
            'body-video': isVideo,
            'body-image': isImage,
            'body-attachment': isAttachment,
            'body-media': isMedia,
            'body-text': isText
          },
          'body'
        ]"
        @touchstart="e => $emit('touchstart', e)"
        @touchend="e => $emit('touchend', e)"
        @mousedown="e => $emit('mousedown', e)"
        @mouseup="e => $emit('mouseup', e)"
      >
        <template v-if="isVideo">
          <video-embed relative auto-initialize :video="videoData" />
        </template>
        <template v-else-if="isImage">
          <img
            v-for="(url, i) in imageUrls"
            :key="i"
            :src="url"
            class="w-100 h-100"
            :id="generateImageKeyForMessage(ts, i)"
            :data-lightbox-group="lightboxGroupKey"
            :data-gallery="`image-gallery-${messageThreadId}`"
            @click="handleSelectImage"
          />
        </template>
        <attachment
          v-else-if="isAttachment"
          :message="message"
          :attachment="message.attachment_data"
          :is-feed="isFeed"
          :user="user"
        />
        <template v-else>
          <span v-html="formattedText" />
        </template>
      </div>
    </div>

    <v-button
      v-if="!isFeed && hasReactions"
      light
      raised
      x-small
      :class="
        `${
          reactionCount === initialReactionCount ? '--no-animation' : ''
        } py-0 px-1 reaction-count p-absolute`
      "
      @click="() => $emit('showReactions')"
    >
      <div :key="reactionCount" class="d-flex align-items-center">
        <v-icon icon="favorite" size="0.75rem"></v-icon>
        <div v-if="reactionCount > 1" class="reaction-count__number">
          {{ reactionCount }}
        </div>
      </div>
    </v-button>
  </div>
</template>

<script>
import { autoLink } from "vue-highlights"

import {
  isTextType,
  isImageType,
  isVideoType,
  isAttachmentType,
  isMediaType,
  formatMentions
} from "@/lib/messages-utils"

import { PROVIDERS } from "@/lib/video-utils"
import { isPresent } from "@/lib/utils"
import { generateImageKeyForMessage } from "@/lib/mk-lightbox"
import { LIGHT_BOX_KEY } from "@/lib/constants"

export default {
  components: {
    VideoEmbed: () => import("@/components/VideoEmbed"),
    Attachment: () => import("@/components/message-thread/Attachment")
  },

  props: {
    messageThreadId: { type: [Number, String], required: true },
    message: { type: Object, required: true },
    isFeed: { type: Boolean, default: false },
    groupKey: { type: String, default: null }
  },

  data() {
    return {
      initialReactionCount: 0,
      lightboxGroupKey: this.groupKey
        ? `${LIGHT_BOX_KEY}-${this.groupKey}`
        : LIGHT_BOX_KEY
    }
  },

  computed: {
    bodyKey() {
      return `body-${this.ts}`
    },

    text() {
      return isPresent(this.message) ? this.message.message : ""
    },

    formattedText() {
      return formatMentions(
        autoLink(this.text, {
          extractUrlsWithoutProtocol: true,
          targetBlank: true,
          urlClass: "message-url"
        })
      )
    },

    ts() {
      return isPresent(this.message) ? this.message.message_ts : null
    },

    isText() {
      return isTextType(this.message)
    },

    isImage() {
      return isImageType(this.message)
    },

    isVideo() {
      return isVideoType(this.message)
    },

    isAttachment() {
      return isAttachmentType(this.message)
    },

    isMedia() {
      return isMediaType(this.message)
    },

    user() {
      return isPresent(this.message.user) ? this.message.user : null
    },

    reactionCount() {
      return isPresent(this.message) ? this.message.reaction_count : 0
    },

    hasReactions() {
      return this.reactionCount > 0
    },

    imageUrls() {
      return isPresent(this.message) ? this.message.image_urls : []
    },

    videoUrl() {
      return isPresent(this.message) && this.isVideo
        ? this.message.video_url
        : null
    },

    isSingleImage() {
      return this.isImage && this.imageUrls.length === 1
    },

    evenNumberOfImages() {
      return this.isImage && this.imageUrls.length % 2 === 0
    },

    videoData() {
      return this.isVideo && isPresent(this.message)
        ? {
            provider: PROVIDERS.MUX,
            embed_link: this.videoUrl
          }
        : null
    },

    attachmentData() {
      return isPresent(this.message) ? this.message.attachment_data : {}
    }
  },

  methods: {
    isPresent,
    generateImageKeyForMessage,

    handleSelectImage(e) {
      this.$emit("selectImage", e.target.id)
    }
  },

  mounted() {
    this.initialReactionCount = this.reactionCount
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";
$post-image--max-height: 22rem;

.message__body {
  position: relative;
  font-size: $size--4;
  margin-left: $size--2;
  word-break: break-word;

  &:hover {
    cursor: pointer;
  }

  .body {
    &:not(.--feed) {
      @include clickable;

      animation: fade-in-up 0.2s ease;
    }

    &.--feed {
      &.body-media {
        margin: 0 -#{$size--3};
        width: calc(100% + $size--3 * 2);
        max-width: none;
        border-radius: 0;
      }

      &.body-image {
        img {
          width: 100%;
          height: auto;
          margin: 0;
          padding: 0;
          max-height: $post-image--max-height;
          border-radius: 0;
        }

        &.--single {
          img {
            object-fit: contain;
          }
        }

        &:not(.--single) {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-gap: $message-image--gap;

          img {
            aspect-ratio: 1;
          }
        }
      }

      &.body-text {
        border-radius: 4px 16px 16px 16px !important;
      }
    }

    overflow: hidden;
    border-radius: 4px;
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .body-text {
    padding: $size--2 $size--3;
    background-color: $color-ui--grey-20;
    color: $color-ui--grey-90;
    white-space: pre-line;
    max-width: $message-text--max-width;
  }

  .body-attachment {
    width: $message-attachment--width;
    overflow: visible;
  }

  .body-image {
    display: flex;
    flex-wrap: wrap;

    &.--single {
      max-width: 30rem;

      img {
        width: auto;
        height: auto;
        max-height: 22rem;
      }
    }

    img {
      width: $message-image--size-sm;
      height: $message-image--size-sm;
      margin-top: $message-image--gap;
      border-radius: 16px;
      object-fit: cover;

      &:not(:last-child) {
        margin-right: $message-image--gap;
      }
    }

    &:not(.--feed) {
      // This max-width for the flex container fit to width of the images,
      // So we can fix the position of the reaction button
      max-width: calc(($message-image--size-sm + $message-image--gap) * 4);

      @media screen and (max-width: calc(($message-image--size-sm + $message-image--gap) * 4 + $size--10)) {
        max-width: calc(($message-image--size-sm + $message-image--gap) * 3);
      }

      @media screen and (max-width: calc(($message-image--size-sm + $message-image--gap) * 3 + $size--10)) {
        max-width: calc(($message-image--size-sm + $message-image--gap) * 2);

        img:nth-child(even) {
          margin-right: 0;
        }
      }

      @media screen and (max-width: calc(($message-image--size-sm + $message-image--gap) * 2 + $size--10)) {
        max-width: calc($message-image--size-sm + $message-image--gap * 2);
      }
    }
  }
}

.reaction-count {
  bottom: -$size--3;
  right: 0;
  z-index: 10;

  overflow: visible;

  .v-icon {
    animation: heart-react 1s ease-in-out;
  }

  &.--no-animation {
    .v-icon {
      animation: none;
    }
  }

  &__number {
    margin-left: 0.2rem;
  }

  &.v-button {
    font-size: $font-size--x-small;
    font-weight: bold;
    min-height: $size--5;
  }

  .v-icon {
    color: $color-ui--red;
    top: 0px;
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.message__body .body {
  &.body-text {
    a {
      color: inherit;
    }

    .mention {
      font-weight: $font-weight--semi-bold;
    }
  }

  &.body-attachment {
    .attachment-image {
      background-color: $color-ui--grey-20;

      &.subscriber-avatar {
        padding: 1rem 0 1rem 1rem;
        background-color: $color-ui--white;

        img {
          object-fit: cover;
          width: 2rem;
          height: 2rem;
        }
      }

      img {
        display: block;
        width: 100%;
        max-height: 190px;
        border-radius: 2px;
        object-fit: contain;
      }
    }
  }

  &.body-video {
    border-radius: 16px;

    .play-icon {
      background: rgba($color-ui--black, 0.56);
      border-radius: 50%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .video-embed .plyr.plyr--video {
      &:fullscreen,
      &:-webkit-full-screen {
        video {
          width: 100%;
          min-height: unset;
          max-height: unset;
        }
      }
    }

    .video-embed__video {
      position: relative;

      > .plyr {
        .plyr__video-wrapper video {
          max-height: 300px;
        }
      }
    }
  }

  &.--feed {
    &.body-attachment {
      .attachment-preview,
      .attachment-item.collection-item > .v-link > .collection-item__content {
        box-shadow: none;
        border-radius: 0;

        .attachment-image {
          display: grid;
          grid-template-columns: 1fr;

          > img {
            max-height: none;
            object-fit: cover;
            aspect-ratio: 2.5;
            border-top: 1px solid $color-ui--grey-20;
          }
        }
      }
    }
  }
}
</style>
